"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ALL_CHAINS, getChainName, getPaymentUrl, ZERO_ADDRESS } from "../../../../anyspend/index.js";
import { CopyToClipboard, ShinyButton, TextLoop, toast } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/index.js";
import { formatTokenAmount } from "../../../../shared/utils/number.js";
import { WalletCoinbase, WalletMetamask, WalletPhantom, WalletTrust } from "@web3icons/react";
import { ChevronLeft, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { memo, useEffect, useMemo, useState } from "react";
import { b3Viem } from "../../../../shared/constants/chains/b3Viem.js";
import { CryptoPaymentMethodType } from "./CryptoPaymentMethod.js";
import { OrderDetailsCollapsible } from "./OrderDetailsCollapsible.js";
import { PaymentMethodSwitch } from "./PaymentMethodSwitch.js";
export const TransferCryptoDetails = memo(function TransferCryptoDetails({
  order,
  srcToken,
  dstToken,
  tournament,
  nft,
  onBack,
  recipientName,
  onPaymentMethodChange,
  classes
}) {
  const [timeLeft, setTimeLeft] = useState(0);
  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!order.expiredAt) return 0;
      const now = Date.now();
      const expiredAt = new Date(order.expiredAt).getTime();
      const diff = Math.max(0, Math.floor((expiredAt - now) / 1000));
      return diff;
    };
    // Set initial time
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [order.expiredAt]);
  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };
  const totalTime = useMemo(() => {
    if (!order.expiredAt) return 15 * 60; // fallback to 15 minutes
    const now_0 = Date.now();
    const expiredAt_0 = new Date(order.expiredAt).getTime();
    const createdAt = order.createdAt ? new Date(order.createdAt).getTime() : now_0;
    return Math.max(0, Math.floor((expiredAt_0 - createdAt) / 1000));
  }, [order.expiredAt, order.createdAt]);
  const roundedUpSrcAmount = useMemo(() => {
    // Display the full transfer amount without rounding since users need to see the exact value they're transferring.
    // Use 21 significant digits (max allowed by Intl.NumberFormat)
    const formattedSrcAmount = srcToken ? formatTokenAmount(BigInt(order.srcAmount), srcToken.decimals, 21, false) : undefined;
    // Simple function to round up the amount (you may need to implement this based on your utils)
    return formattedSrcAmount;
  }, [order.srcAmount, srcToken]);
  const expectedDstAmount = order.type === "mint_nft" || order.type === "join_tournament" || order.type === "fund_tournament" || order.type === "custom" ? "0" : order.payload.expectedDstAmount.toString();
  const formattedExpectedDstAmount = formatTokenAmount(BigInt(expectedDstAmount), dstToken.decimals);
  const handleCopyAddress = () => {
    navigator.clipboard.writeText(order.globalAddress);
    toast.success("Deposit address copied to clipboard");
  };
  return _jsxs("div", {
    className: classes?.container || "order-transfer-crypto flex w-full flex-col gap-6",
    children: [_jsxs("div", {
      className: classes?.header || "order-transfer-crypto-header flex items-center justify-between",
      children: [_jsx("button", {
        onClick: onBack,
        className: classes?.backButton || "order-transfer-crypto-back-btn text-as-primary/60 hover:text-as-primary flex h-10 w-10 items-center justify-center rounded-full transition-colors",
        children: _jsx(ChevronLeft, {
          size: 24,
          className: "text-as-quaternary"
        })
      }), _jsx("h2", {
        className: classes?.title || "order-transfer-crypto-title text-as-primary text-lg font-semibold",
        children: "Transfer crypto"
      }), _jsxs("div", {
        className: "order-transfer-crypto-timer relative flex h-11 w-11 items-center justify-center",
        children: [_jsxs("svg", {
          className: "order-transfer-crypto-timer-svg h-11 w-11 -rotate-90",
          viewBox: "0 0 44 44",
          children: [_jsx("circle", {
            cx: "22",
            cy: "22",
            r: "18",
            stroke: "currentColor",
            strokeWidth: "3",
            fill: "none",
            className: "order-transfer-crypto-timer-bg text-gray-200"
          }), _jsx("circle", {
            cx: "22",
            cy: "22",
            r: "18",
            stroke: "currentColor",
            strokeWidth: "3",
            fill: "none",
            strokeLinecap: "round",
            className: "order-transfer-crypto-timer-progress text-blue-500",
            strokeDasharray: `${2 * Math.PI * 18}`,
            strokeDashoffset: `${2 * Math.PI * 18 * (1 - timeLeft / totalTime)}`,
            style: {
              transition: "stroke-dashoffset 1s linear"
            }
          })]
        }), _jsx("div", {
          className: "order-transfer-crypto-timer-text absolute inset-0 flex items-center justify-center",
          children: _jsx("span", {
            className: "text-as-primary text-[10px] font-semibold",
            children: formatTime(timeLeft)
          })
        })]
      })]
    }), _jsxs("div", {
      className: "order-transfer-crypto-content flex w-full flex-col gap-4",
      children: [_jsxs("div", {
        className: "order-transfer-crypto-cards flex items-center gap-4",
        children: [_jsxs("div", {
          className: "order-transfer-crypto-amount-card w-full",
          children: [_jsx("span", {
            className: "order-transfer-crypto-amount-label text-as-content-secondary text-sm font-medium",
            children: "Amount"
          }), _jsx("div", {
            className: "order-transfer-crypto-amount-container border-as-border-primary rounded-lg border p-2 shadow-sm",
            children: _jsx(CopyToClipboard, {
              text: roundedUpSrcAmount || "",
              onCopy: () => {
                toast.success("Amount copied to clipboard");
              },
              children: _jsxs("div", {
                className: "order-transfer-crypto-amount-copy flex cursor-pointer items-center justify-between gap-2",
                children: [_jsxs("strong", {
                  className: "order-transfer-crypto-amount-text text-as-primary font-semibold",
                  children: [roundedUpSrcAmount, " ", srcToken.symbol]
                }), _jsx(Copy, {
                  className: "order-transfer-crypto-amount-copy-icon text-as-primary/50 hover:text-as-primary h-4 w-4 transition-all duration-200"
                })]
              })
            })
          })]
        }), _jsxs("div", {
          className: "order-transfer-crypto-chain-card w-full",
          children: [_jsx("span", {
            className: "order-transfer-crypto-chain-label text-as-content-secondary text-sm font-medium",
            children: "Chain"
          }), _jsx("div", {
            className: "order-transfer-crypto-chain-container border-as-border-primary rounded-lg border p-2 shadow-sm",
            children: _jsxs("div", {
              className: "order-transfer-crypto-chain-info flex items-center gap-2",
              children: [_jsx("img", {
                src: ALL_CHAINS[order.srcChain].logoUrl,
                alt: getChainName(order.srcChain),
                className: cn("order-transfer-crypto-chain-logo h-6 rounded-full", order.srcChain === b3Viem.id && "h-5 rounded-none")
              }), _jsx("span", {
                className: "order-transfer-crypto-chain-name text-as-primary text-sm font-semibold",
                children: getChainName(order.srcChain)
              })]
            })
          })]
        })]
      }), _jsxs("div", {
        className: "order-transfer-crypto-qr-deposit-card border-b3-react-border bg-as-surface-secondary grid h-[220px] grid-cols-2 overflow-hidden rounded-xl border",
        children: [_jsx("div", {
          className: "order-transfer-crypto-qr-section border-as-border-primary h-full w-full border-r",
          children: _jsx("div", {
            className: "order-transfer-crypto-qr-wrapper flex justify-center",
            children: _jsxs("div", {
              className: "order-transfer-crypto-qr-container bg-as-surface-secondary flex flex-col items-center rounded-lg p-6",
              children: [_jsx(QRCodeSVG, {
                value: getPaymentUrl(order.globalAddress, BigInt(order.srcAmount), order.srcTokenAddress === ZERO_ADDRESS ? "ETH" : order.srcTokenAddress, order.srcChain, srcToken?.decimals),
                className: "order-transfer-crypto-qr-code bg-as-surface-secondary max-h-48 max-w-48"
              }), _jsxs("div", {
                className: "order-transfer-crypto-wallet-hint mt-3 flex items-center justify-center gap-2 text-sm",
                children: [_jsx("span", {
                  className: "order-transfer-crypto-wallet-text text-as-brand/70 text-sm font-medium",
                  children: "SCAN WITH"
                }), _jsxs(TextLoop, {
                  interval: 3,
                  children: [_jsx(WalletMetamask, {
                    className: "order-transfer-crypto-wallet-icon h-5 w-5",
                    variant: "branded"
                  }), _jsx(WalletCoinbase, {
                    className: "order-transfer-crypto-wallet-icon h-5 w-5",
                    variant: "branded"
                  }), _jsx(WalletPhantom, {
                    className: "order-transfer-crypto-wallet-icon h-5 w-5",
                    variant: "branded"
                  }), _jsx(WalletTrust, {
                    className: "order-transfer-crypto-wallet-icon h-5 w-5",
                    variant: "branded"
                  })]
                })]
              })]
            })
          })
        }), _jsxs("div", {
          className: "order-transfer-crypto-address-section flex h-full w-full flex-col gap-2 p-6",
          children: [_jsx("span", {
            className: "order-transfer-crypto-address-label text-as-content-secondary text-sm font-medium",
            children: "Deposit address:"
          }), _jsxs("div", {
            className: "order-transfer-crypto-address-copy flex h-full cursor-pointer flex-col items-stretch justify-between gap-4",
            onClick: handleCopyAddress,
            children: [_jsx("div", {
              className: "order-transfer-crypto-address-text text-as-primary break-all font-mono text-sm font-semibold leading-relaxed",
              children: order.globalAddress
            }), _jsx("div", {
              className: "order-transfer-crypto-address-copy-icon-wrapper place-self-end",
              children: _jsx(Copy, {
                className: "order-transfer-crypto-address-copy-icon group-hover:text-as-brand text-as-tertiary h-4 w-4 cursor-pointer transition-all duration-200"
              })
            })]
          })]
        })]
      }), _jsx(OrderDetailsCollapsible, {
        order: order,
        dstToken: dstToken,
        tournament: tournament,
        nft: nft,
        recipientName: recipientName,
        formattedExpectedDstAmount: formattedExpectedDstAmount
      })]
    }), _jsxs("div", {
      className: "order-transfer-crypto-actions flex flex-col gap-3",
      children: [_jsx(ShinyButton, {
        accentColor: "hsl(var(--as-brand))",
        textColor: "text-white",
        className: "order-transfer-crypto-copy-btn w-full py-3",
        onClick: handleCopyAddress,
        children: "Copy deposit address"
      }), _jsx(PaymentMethodSwitch, {
        currentMethod: CryptoPaymentMethodType.TRANSFER_CRYPTO,
        onMethodChange: onPaymentMethodChange
      })]
    })]
  });
});